import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search, ChevronRight } from 'lucide-react';
import { Input } from "@/Components/ui/input";
import { Button } from "@/Components/ui/button";
import axiosInstance from '../../utils/axiosConfig';
import PropTypes from 'prop-types';

const DishModal = ({ isOpen, onClose, onSave }) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [dishes, setDishes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const handleDishSelect = async (dish) => {
        try {
            const returnDish = {
                id: dish.id,
                name: dish.name,
                price: dish.price
            };

            onSave(returnDish);
            setSearchTerm('');
            setDishes([]);
            onClose();
        } catch (error) {
            console.error('Error adding product:', error);
        }
    };

    const handleSearch = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/dish/Uncategorised${searchTerm ? `?keyword=${searchTerm}` : ''}`);
            setDishes(response.data);
        } catch (error) {
            console.error('Error fetching dishes:', error);
            setDishes([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Zoek direct bij openen en wanneer searchTerm verandert
    useEffect(() => {
        if (!isOpen) return;

        const delayDebounce = setTimeout(() => {
            handleSearch();
        }, searchTerm ? 500 : 0); // Direct zoeken als searchTerm leeg is

        return () => clearTimeout(delayDebounce);
    }, [searchTerm, isOpen]);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg w-full max-w-7xl p-6 relative max-h-[80vh] min-h-[40vh] overflow-hidden flex flex-col">
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
                >
                    <X size={24} />
                </button>

                <h2 className="text-xl font-inter text-ps-blue-dark mb-4">
                    {t('modals.addDish')}
                </h2>

                <div className="relative mb-4">
                    <Input
                        type="text"
                        placeholder={t('modals.searchDish')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="pl-10"
                    />
                    <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />

                    <Button
                        onClick={handleSearch}
                        className="inline-flex absolute right-0 top-0 items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-ps-blue-dark hover:bg-ps-blue-dark/90 text-white px-8 py-2 rounded"
                    >
                        <span>{t('products.search')}</span>
                    </Button>
                </div>

                <div className="flex-1 overflow-y-auto space-y-6">
                    {isLoading ? (
                        <div className="text-center py-4">{t('common.loading')}</div>
                    ) : (
                        <section>
                            <h3 className="font-inter text-ps-blue-dark mb-2">{t('dishes.title')}</h3>
                            <div className="space-y-2">
                                {dishes && dishes.length > 0 ? (
                                    dishes.map((gerecht) => (
                                        <div
                                            key={gerecht.id}
                                            onClick={() => handleDishSelect(gerecht)}
                                            className="flex items-center justify-between w-full p-4 hover:pl-6 cursor-pointer border-b-[1px]"
                                        >
                                            <span className="font-inter">{gerecht.name}</span>
                                            <div className="flex items-center gap-4">
                                                <ChevronRight className="text-ps-blue-dark" size={20} />
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-4 text-gray-500">
                                        {t('modals.noDishesFound')}
                                    </div>
                                )}
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
};

DishModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired
};

export default DishModal;